import { createStore, type StoreApi } from 'zustand/vanilla';
import { FinalidadeEstado, type AvisoMudancaEstado, type GrupoDto, type ProcessoCartaoDto, type QuadroDto } from '@nexital/shared';
import type { Ponto } from '../domain/camera';
import { nivelDetalhe, type NivelDetalhe } from '../domain/camera';
import { ErroApi, type ApiQuadro } from './portas';

export type Salvamento = 'salvo' | 'salvando' | 'erro' | 'conflito' | 'offline';
export type GeometriaGrupo = Pick<GrupoDto, 'x' | 'y' | 'largura' | 'altura' | 'rotacao'>;
export type AlteracaoGrupo = GeometriaGrupo & { uuid: string };

/** Posição relativa ao grupo do estado de destino; absoluta quando o destino não tem grupo. */
export type PedidoMudanca = {
  processoUuid: string;
  estadoUuid: string;
  posicao: Ponto;
  motivoUuid?: string;
  motivoTexto?: string;
  confirmarAvisos?: boolean;
  /** Grupos que crescem para receber o cartão: gravam junto com a mudança (RQ-EST-005). */
  grupos?: AlteracaoGrupo[];
};

/** "avisos": nada foi gravado; a tela pergunta e reenvia com confirmarAvisos, ou desfaz a mudança otimista. */
export type ResultadoMudanca =
  | { tipo: 'gravado' }
  | { tipo: 'falhou' }
  | { tipo: 'avisos'; avisos: AvisoMudancaEstado[]; desfazer: () => void };

/** Estados cujos processos aparecem no quadro (cartões nos grupos ou na bandeja Sem estado). */
const NO_QUADRO = [FinalidadeEstado.Novo, FinalidadeEstado.Fluxo, FinalidadeEstado.SemEstado];

export type EstadoQuadro = {
  dados: QuadroDto | null;
  erroCarga: string | null;
  salvamento: Salvamento;
  gravacoesPendentes: number;
  /** Gravações feitas sem conexão, à espera de reenvio (RQ-COL-006, RQ-COL-007). */
  pendentesOffline: number;
  /** Enquanto a pessoa arrasta, releituras pedidas pelo tempo real esperam o fim do arraste. */
  emArraste: boolean;
  zoom: number;
  nivel: NivelDetalhe;
  carregar(): Promise<void>;
  /** Relê o quadro (evento de outra pessoa); durante um arraste, só depois de soltar. */
  recarregar(): void;
  definirEmArraste(emArraste: boolean): void;
  reenviarPendentes(): Promise<void>;
  definirZoom(zoom: number): void;
  moverProcesso(uuid: string, posicao: Ponto, grupos?: AlteracaoGrupo[]): Promise<void>;
  alterarGrupos(alteracoes: AlteracaoGrupo[]): Promise<void>;
  mudarEstado(pedido: PedidoMudanca): Promise<ResultadoMudanca>;
  restaurar(processoUuid: string): Promise<void>;
  /** Exclui da Lixeira um processo ou todos (RQ-EST-012). */
  excluirDaLixeira(processoUuids: string[]): Promise<void>;
};

type Loja = Pick<StoreApi<EstadoQuadro>, 'setState' | 'getState'>;
type Trabalho = () => Promise<void>;
type Ambiente = { api: ApiQuadro; quadroUuid: string; loja: Loja; fila: Trabalho[]; releituraPendente: boolean };

/** Estado do quadro na tela. Gravações são otimistas: a tela muda já, e recarrega se o servidor recusar. */
export function criarStoreQuadro(api: ApiQuadro, quadroUuid: string) {
  return createStore<EstadoQuadro>()((setState, getState) => {
    const ambiente: Ambiente = { api, quadroUuid, loja: { setState, getState }, fila: [], releituraPendente: false };
    return {
      dados: null,
      erroCarga: null,
      salvamento: 'salvo',
      gravacoesPendentes: 0,
      pendentesOffline: 0,
      emArraste: false,
      zoom: 1,
      nivel: 'completo',
      carregar: () => carregar(ambiente),
      recarregar: () => {
        if (getState().emArraste) ambiente.releituraPendente = true;
        else void carregar(ambiente);
      },
      definirEmArraste: (emArraste) => {
        setState({ emArraste });
        if (emArraste || !ambiente.releituraPendente) return;
        ambiente.releituraPendente = false;
        void carregar(ambiente);
      },
      reenviarPendentes: () => reenviarPendentes(ambiente),
      definirZoom: (zoom) => {
        const nivel = nivelDetalhe(zoom);
        setState(getState().nivel === nivel ? { zoom } : { zoom, nivel });
      },
      moverProcesso: (uuid, posicao, grupos) => moverProcesso(ambiente, uuid, posicao, grupos),
      alterarGrupos: (alteracoes) => alterarGrupos(ambiente, alteracoes),
      mudarEstado: (pedido) => mudarEstado(ambiente, pedido),
      restaurar: (uuid) => restaurar(ambiente, uuid),
      excluirDaLixeira: (uuids) => excluirDaLixeira(ambiente, uuids),
    };
  });
}

async function carregar({ api, quadroUuid, loja }: Ambiente) {
  try {
    loja.setState({ dados: await api.obterQuadro(quadroUuid), erroCarga: null });
  } catch (erro) {
    loja.setState({ erroCarga: (erro as Error).message });
  }
}

/**
 * Conta gravações em andamento para o indicador de salvamento; em falha, recarrega do servidor.
 * Sem conexão (falha de rede), a gravação vai para a fila e a tela continua como está (RQ-COL-006).
 * Devolve se o trabalho terminou sem erro (ou ficou na fila).
 */
async function gravar(ambiente: Ambiente, trabalho: Trabalho): Promise<boolean> {
  const { loja } = ambiente;
  loja.setState((s) => ({ gravacoesPendentes: s.gravacoesPendentes + 1, salvamento: 'salvando' }));
  try {
    await trabalho();
    loja.setState((s) => ({ salvamento: s.gravacoesPendentes > 1 ? 'salvando' : 'salvo' }));
    return true;
  } catch (erro) {
    if (erro instanceof TypeError) {
      ambiente.fila.push(trabalho);
      loja.setState({ salvamento: 'offline', pendentesOffline: ambiente.fila.length });
      return true;
    }
    loja.setState({ salvamento: erro instanceof ErroApi && erro.status === 409 ? 'conflito' : 'erro' });
    await carregar(ambiente);
    return false;
  } finally {
    loja.setState((s) => ({ gravacoesPendentes: s.gravacoesPendentes - 1 }));
  }
}

/** Alterações de grupos com a versão que a tela conhece, para o controle de versão no servidor. */
function comVersoes(loja: Loja, alteracoes: AlteracaoGrupo[]) {
  const versaoAtual = new Map(loja.getState().dados?.grupos.map((g) => [g.uuid, g.versao]));
  return alteracoes.map((a) => ({ ...a, versao: versaoAtual.get(a.uuid) ?? 0 }));
}

/** O crescimento dos grupos (se houver) grava na mesma chamada do cartão e aparece quando ela confirma. */
/** Conexão de volta: reenvia em ordem o que foi feito offline e relê o quadro. */
async function reenviarPendentes(ambiente: Ambiente) {
  const fila = ambiente.fila.splice(0);
  if (!fila.length) return;
  ambiente.loja.setState({ pendentesOffline: 0 });
  for (const trabalho of fila) await gravar(ambiente, trabalho);
  if (!ambiente.fila.length) await carregar(ambiente);
}

async function moverProcesso(ambiente: Ambiente, uuid: string, posicao: Ponto, alteracoes: AlteracaoGrupo[] = []) {
  const { api, quadroUuid, loja } = ambiente;
  const processo = loja.getState().dados?.processos.find((p) => p.uuid === uuid);
  if (!processo) return;
  const grupos = alteracoes.length ? comVersoes(loja, alteracoes) : undefined;
  loja.setState((s) => comProcesso(s, uuid, posicao));
  await gravar(ambiente, async () => {
    const r = await api.moverProcesso(quadroUuid, uuid, { ...posicao, versao: processo.versao, grupos });
    loja.setState((s) => comGrupos(comProcesso(s, uuid, { versao: r.versao }), alteracoes, r.versoesGrupos ?? {}));
  });
}

async function alterarGrupos(ambiente: Ambiente, alteracoes: AlteracaoGrupo[]) {
  const { api, quadroUuid, loja } = ambiente;
  const grupos = comVersoes(loja, alteracoes);
  await gravar(ambiente, async () => {
    const { versoes } = await api.alterarGrupos(quadroUuid, { grupos });
    loja.setState((s) => comGrupos(s, alteracoes, versoes));
  });
}

/**
 * Muda o estado na tela já (otimista) e grava. Se o destino sai do quadro (Arquivo, Lixeira), o cartão some e,
 * depois de gravado, o quadro é recarregado (lista da Lixeira). Com avisos (422), nada foi gravado no servidor.
 */
async function mudarEstado(ambiente: Ambiente, pedido: PedidoMudanca): Promise<ResultadoMudanca> {
  const { api, quadroUuid, loja } = ambiente;
  const dados = loja.getState().dados;
  const original = dados?.processos.find((p) => p.uuid === pedido.processoUuid);
  if (!dados || !original) return { tipo: 'falhou' };
  const { processoUuid, estadoUuid, posicao, grupos: alteracoes = [], ...opcoes } = pedido;
  const grupos = alteracoes.length ? comVersoes(loja, alteracoes) : undefined;
  const ficaNoQuadro = dados.estados.some((e) => e.uuid === estadoUuid && NO_QUADRO.includes(e.finalidade));
  const desfazer = () => loja.setState((s) => comProcessoOriginal(s, original));
  loja.setState((s) => (ficaNoQuadro ? comProcesso(s, processoUuid, { estadoUuid, ...posicao }) : semProcesso(s, processoUuid)));

  let avisos: AvisoMudancaEstado[] = [];
  const gravou = await gravar(ambiente, async () => {
    try {
      const entrada = { estadoUuid, ...posicao, versao: original.versao, grupos, ...opcoes };
      const r = await api.mudarEstado(quadroUuid, processoUuid, entrada);
      const novos = r.versoesGrupos ?? {};
      if (ficaNoQuadro) loja.setState((s) => comGrupos(comProcesso(s, processoUuid, { versao: r.versao }), alteracoes, novos));
      else await carregar(ambiente);
    } catch (erro) {
      if (!(erro instanceof ErroApi && erro.status === 422 && erro.avisos.length)) throw erro;
      avisos = erro.avisos;
    }
  });
  if (avisos.length) return { tipo: 'avisos', avisos, desfazer };
  return gravou ? { tipo: 'gravado' } : { tipo: 'falhou' };
}

/** Tira da Lixeira (o servidor decide o estado de volta) e recarrega o quadro. */
async function restaurar(ambiente: Ambiente, processoUuid: string) {
  const { api, quadroUuid, loja } = ambiente;
  const item = loja.getState().dados?.lixeira.find((p) => p.uuid === processoUuid);
  if (!item) return;
  await gravar(ambiente, async () => {
    await api.restaurarProcesso(quadroUuid, processoUuid, { versao: item.versao });
    await carregar(ambiente);
  });
}

async function excluirDaLixeira(ambiente: Ambiente, processoUuids: string[]) {
  const { api, quadroUuid } = ambiente;
  if (!processoUuids.length) return;
  await gravar(ambiente, async () => {
    await api.esvaziarLixeira(quadroUuid, { processos: processoUuids });
    await carregar(ambiente);
  });
}

function semProcesso(s: EstadoQuadro, uuid: string): Partial<EstadoQuadro> {
  if (!s.dados) return {};
  return { dados: { ...s.dados, processos: s.dados.processos.filter((p) => p.uuid !== uuid) } };
}

/** Devolve o processo como era antes da mudança otimista (só ele: grupos e outros cartões ficam como estão). */
function comProcessoOriginal(s: EstadoQuadro, original: ProcessoCartaoDto): Partial<EstadoQuadro> {
  if (!s.dados) return {};
  const processos = [...s.dados.processos.filter((p) => p.uuid !== original.uuid), original];
  return { dados: { ...s.dados, processos } };
}

function comProcesso(s: EstadoQuadro, uuid: string, mudancas: Partial<ProcessoCartaoDto>): Pick<EstadoQuadro, 'dados'> {
  if (!s.dados) return { dados: null };
  return { dados: { ...s.dados, processos: s.dados.processos.map((p) => (p.uuid === uuid ? { ...p, ...mudancas } : p)) } };
}

function comGrupos(s: Pick<EstadoQuadro, 'dados'>, alteracoes: AlteracaoGrupo[], versoes: Record<string, number>): Partial<EstadoQuadro> {
  if (!s.dados || !alteracoes.length) return s;
  const porUuid = new Map(alteracoes.map((a) => [a.uuid, a]));
  const grupos = s.dados.grupos.map((g) => {
    const nova = porUuid.get(g.uuid);
    return nova ? { ...g, ...nova, versao: versoes[g.uuid] ?? g.versao } : g;
  });
  return { dados: { ...s.dados, grupos } };
}

export type StoreQuadro = ReturnType<typeof criarStoreQuadro>;
